import { Component, ElementRef, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';

@Component({
	selector: 'app-particle-background',
	template: `<canvas #canvas class="particle-canvas"></canvas>`,
	styles: [
		`
			.particle-canvas {
				position: fixed;
				top: 0;
				left: 0;
				width: 100vw;
				height: 100vh;
				z-index: -1;
			}
		`,
	],
	standalone: true,
})
export class ParticleBackgroundComponent implements OnInit, OnDestroy {
	@ViewChild('canvas', { static: true })
	canvasRef!: ElementRef<HTMLCanvasElement>;
	private ctx!: CanvasRenderingContext2D;
	private particles: Particle[] = [];
	private animationId!: number;
	private readonly particleConnectDist: number = 200;
	private readonly mouseCollisionDist: number = 40;
	private resizeTimeout: any;

	private mouseX: number = 0;
	private mouseY: number = 0;

	private boidsMode = false;

	// Pop-in state
	private popInStartTime: number = 0;
	private popInDuration: number = 20000; // ms
	private targetParticleCount: number = 0;

	ngOnInit() {
		this.setupCanvas();
		this.startPopIn();
		this.animate();
	}

	ngOnDestroy() {
		cancelAnimationFrame(this.animationId);
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}
	}

	@HostListener('window:resize')
	onWindowResize() {
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}

		this.resizeTimeout = setTimeout(() => {
			this.handleResize();
		}, 100);
	}

	@HostListener('window:mousemove', ['$event'])
	onMouseMove(event: MouseEvent) {
		this.mouseX = event.clientX;
		this.mouseY = event.clientY;
	}

	@HostListener('window:keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (event.ctrlKey && event.key.toLowerCase() === 'b') {
			this.toggleBoidsMode();
		}
	}

	private toggleBoidsMode() {
		this.boidsMode = !this.boidsMode;

		const targetBoids = Math.ceil(this.particles.length * 0.95);

		if (this.boidsMode) {
			// Assign most as boids, a few as predators
			this.particles.forEach((p, i) => {
				p.type = i < targetBoids ? 'boid' : 'predator';
			});
		} else {
			// Reset to normal
			this.particles.forEach((p) => (p.type = 'normal'));
		}
	}

	private handleResize() {
		this.setupCanvas();
		// Remove particles outside new bounds
		this.particles = this.particles.filter((particle) => particle.x >= 0 && particle.x <= window.innerWidth && particle.y >= 0 && particle.y <= window.innerHeight);

		// Adjust particle count for new screen size
		this.targetParticleCount = Math.floor((window.innerWidth * window.innerHeight) / 20000);

		// If we need more, restart pop-in for the new ones
		if (this.particles.length < this.targetParticleCount) {
			this.popInStartTime = performance.now();
		}

		// If too many, trim
		if (this.particles.length > this.targetParticleCount) {
			this.particles.splice(this.targetParticleCount);
		}
	}

	private setupCanvas() {
		const canvas = this.canvasRef.nativeElement;
		this.ctx = canvas.getContext('2d')!;
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
		canvas.style.width = `${window.innerWidth}px`;
		canvas.style.height = `${window.innerHeight}px`;
	}

	private startPopIn() {
		this.particles = [];
		this.targetParticleCount = Math.floor((window.innerWidth * window.innerHeight) / 20000);
		this.popInStartTime = performance.now();
	}

	private animate = () => {
		const width = window.innerWidth;
		const height = window.innerHeight;

		// --- Background gradient with brightness animation ---
		const time = performance.now() / 1000; // seconds
		const brightness = 0.4 + Math.sin(time * 0.2) * 0.15;

		const gradient = this.ctx.createLinearGradient(0, 0, width, height);
		gradient.addColorStop(0, this.adjustBrightness('#667eea', brightness));
		gradient.addColorStop(1, this.adjustBrightness('#764ba2', brightness));

		this.ctx.fillStyle = gradient;
		this.ctx.fillRect(0, 0, width, height);

		// --- Gradually add particles ---
		if (this.particles.length < this.targetParticleCount) {
			const now = performance.now();
			const elapsed = now - this.popInStartTime;
			const progress = Math.min(elapsed / this.popInDuration, 1);
			const shouldHave = Math.floor(this.targetParticleCount * progress);

			while (this.particles.length < shouldHave) {
				this.particles.push(new Particle(Math.random() * width, Math.random() * height, this.mouseCollisionDist));
			}
		}

		// --- Update and draw particles ---
		this.particles.forEach((particle) => {
			if (this.boidsMode) {
				particle.updateBoid(this.particles, width, height);
			} else {
				particle.update(width, height, this.mouseX, this.mouseY);
			}
			particle.draw(this.ctx, this.boidsMode);
		});

		// --- Mouse collision area ---
		this.ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
		this.ctx.beginPath();
		this.ctx.arc(this.mouseX, this.mouseY, this.mouseCollisionDist, 0, Math.PI * 2);
		this.ctx.fill();

		// --- Connections ---
		if (!this.boidsMode) {
			this.drawConnections();
		}

		this.animationId = requestAnimationFrame(this.animate);
	};

	private drawConnections() {
		const time = performance.now();

		// Track which particles should spark
		const sparkParticles = new Set<Particle>();

		for (let i = 0; i < this.particles.length; i++) {
			for (let j = i + 1; j < this.particles.length; j++) {
				const p1 = this.particles[i];
				const p2 = this.particles[j];
				const distance = p1.distanceTo(p2);

				if (distance < this.particleConnectDist) {
					this.ctx.save(); // Save state so styles don't leak

					// --- Flow effect (moving dashes) ---
					this.ctx.setLineDash([4, 8]);
					this.ctx.lineDashOffset = (time / 50) % 12;

					// Base style
					this.ctx.strokeStyle = 'rgba(255, 255, 255, ' + (1 - distance / this.particleConnectDist) + ')';
					this.ctx.lineWidth = 1;
					this.ctx.shadowBlur = 0;
					this.ctx.shadowColor = 'transparent';

					// --- Sparks when very close ---
					if (distance < 60) {
						this.ctx.strokeStyle = 'yellow';

						const minWidth = 1;
						const maxWidth = 10;
						const t = 1 - distance / 60; // 1 when close, 0 when at max spark distance
						this.ctx.lineWidth = minWidth + (maxWidth - minWidth) * t;

						this.ctx.shadowBlur = 10;
						this.ctx.shadowColor = 'yellow';

						// Mark both particles for spark effect
						sparkParticles.add(p1);
						sparkParticles.add(p2);
					}

					// Draw connection
					this.ctx.beginPath();
					this.ctx.moveTo(p1.x, p1.y);
					this.ctx.lineTo(p2.x, p2.y);
					this.ctx.stroke();

					this.ctx.restore(); // Restore state
				}
			}
		}

		// Draw spark effect on particles that were in a spark connection
		sparkParticles.forEach((p) => {
			this.ctx.save();
			this.ctx.fillStyle = 'yellow';
			this.ctx.shadowBlur = 15;
			this.ctx.shadowColor = 'yellow';
			this.ctx.beginPath();
			this.ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
			this.ctx.fill();
			this.ctx.restore();
		});
	}

	private adjustBrightness(hex: string, factor: number): string {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);

		const nr = Math.min(255, Math.max(0, r * factor));
		const ng = Math.min(255, Math.max(0, g * factor));
		const nb = Math.min(255, Math.max(0, b * factor));

		return `rgb(${nr}, ${ng}, ${nb})`;
	}
}

class Particle {
	vx = (Math.random() - 0.5) * 0.5;
	vy = (Math.random() - 0.5) * 0.5;
	type: string = 'normal';

	constructor(
		public x: number,
		public y: number,
		public mouseCollisionRadius: number,
	) {}

	update(canvasWidth: number, canvasHeight: number, mouseX: number, mouseY: number) {
		const xRawDiff = this.x - mouseX;
		const yRawDiff = this.y - mouseY;
		const mouseCollisionDist = Math.sqrt(xRawDiff * xRawDiff + yRawDiff * yRawDiff);

		if (mouseCollisionDist <= this.mouseCollisionRadius) {
			this.vx = (xRawDiff * 20) / Math.pow(mouseCollisionDist, 2);
			this.vy = (yRawDiff * 20) / Math.pow(mouseCollisionDist, 2);
		}

		this.x += this.vx;
		this.y += this.vy;

		// Bounce off edges with bounds checking
		if (this.x <= 0 || this.x >= canvasWidth) {
			this.vx *= -1;
			this.x = Math.max(0, Math.min(canvasWidth, this.x));
		}

		if (this.y <= 0 || this.y >= canvasHeight) {
			this.vy *= -1;
			this.y = Math.max(0, Math.min(canvasHeight, this.y));
		}
	}

	updateBoid(particles: Particle[], canvasWidth: number, canvasHeight: number) {
		const perceptionRadius = 50;
		let total = 0;
		let avgX = 0,
			avgY = 0;
		let avgVX = 0,
			avgVY = 0;
		let avoidX = 0,
			avoidY = 0;

		particles.forEach((other) => {
			if (other === this) return;
			const dist = this.distanceTo(other);
			if (dist < perceptionRadius) {
				if (this.type === 'boid' && other.type === 'boid') {
					avgX += other.x;
					avgY += other.y;
					avgVX += other.vx;
					avgVY += other.vy;
					total++;
				}
				if (dist < 20) {
					// separation
					avoidX += this.x - other.x;
					avoidY += this.y - other.y;
				}
				if (this.type === 'predator' && other.type === 'boid') {
					// chase
					this.vx += (other.x - this.x) * 0.001;
					this.vy += (other.y - this.y) * 0.001;
				}
				if (this.type === 'boid' && other.type === 'predator') {
					// flee
					this.vx += (this.x - other.x) * 0.002;
					this.vy += (this.y - other.y) * 0.002;
				}
			}
		});

		if (total > 0 && this.type === 'boid') {
			avgX /= total;
			avgY /= total;
			avgVX /= total;
			avgVY /= total;

			// Cohesion
			this.vx += (avgX - this.x) * 0.001;
			this.vy += (avgY - this.y) * 0.001;

			// Alignment
			this.vx += (avgVX - this.vx) * 0.05;
			this.vy += (avgVY - this.vy) * 0.05;

			// Separation
			this.vx += avoidX * 0.05;
			this.vy += avoidY * 0.05;
		}

		// Limit speed
		const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
		const maxSpeed = 2;
		if (speed > maxSpeed) {
			this.vx = (this.vx / speed) * maxSpeed;
			this.vy = (this.vy / speed) * maxSpeed;
		}

		// Move and bounce
		this.x += this.vx;
		this.y += this.vy;
		if (this.x <= 0 || this.x >= canvasWidth) this.vx *= -1;
		if (this.y <= 0 || this.y >= canvasHeight) this.vy *= -1;
	}

	draw(ctx: CanvasRenderingContext2D, boidsMode: boolean) {
		if (this.type === 'boid') ctx.fillStyle = 'limegreen';
		else if (this.type === 'predator') ctx.fillStyle = 'red';
		else ctx.fillStyle = 'white';

		if (boidsMode && (this.type === 'boid' || this.type === 'predator')) {
			const angle = Math.atan2(this.vy, this.vx);
			const size = this.type === 'predator' ? 12 : 10;

			ctx.beginPath();
			ctx.moveTo(this.x + Math.cos(angle) * size, this.y + Math.sin(angle) * size);
			ctx.lineTo(this.x + Math.cos(angle + Math.PI * 0.75) * size * 0.6, this.y + Math.sin(angle + Math.PI * 0.75) * size * 0.6);
			ctx.lineTo(this.x + Math.cos(angle - Math.PI * 0.75) * size * 0.6, this.y + Math.sin(angle - Math.PI * 0.75) * size * 0.6);
			ctx.closePath();
			ctx.fill();
		} else {
			// Normal circle
			ctx.beginPath();
			ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
			ctx.fill();
		}
	}

	distanceTo(other: Particle): number {
		return Math.sqrt(Math.pow(this.x - other.x, 2) + Math.pow(this.y - other.y, 2));
	}
}
